import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { backend, BlockEntity, displayDosage } from "../../Backend";
import Flex from "../../components/Flex";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchConcepts, selectConceptCache } from "../../redux/slices/lensEditor";
import WitsConceptInfo from "./WitsConceptInfo";
import { flatten } from "../../helpers/coll";
import AnnotatedBlock from "routes/lenses/lens/case/AnnotatedBlock";
import { P } from "../../theme/typography";

export interface DetectionSandboxProps {
  detectionResult: BlockEntity[];
  setDetectionResult: React.Dispatch<React.SetStateAction<BlockEntity[]>>;
}
const DetectionSandbox = (props: DetectionSandboxProps) => {
  const { detectionResult, setDetectionResult } = props;
  const [grams, setGrams] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const [query, setQuery] = React.useState("");
  const conceptCache = useAppSelector(selectConceptCache);

  const getMedicalEntities = async () => {
    if (query) {
      const response = await backend.detectEntities(query);
      const entities = response?.umlsEntities || [];
      setGrams(response?.grams ?? []);
      const conceptIds = flatten(entities.map((ent) => ent.entity.id));
      dispatch(fetchConcepts({ conceptIds }));
      setDetectionResult(entities);
    }
  };

  const additionalFields = (entity: BlockEntity) =>
    flatten([
      entity.dosage ? ["Dosage", displayDosage(entity.dosage)] : undefined,
      entity.medQuantity ? ["Quantity", entity.medQuantity.toString()] : undefined,
      entity.ndcCode ? ["NDC Code", entity.ndcCode] : undefined,
      entity.medTimesPerDay ? ["Times Per Day", entity.medTimesPerDay.toString()] : undefined,
      entity.medDaySupply ? ["Days Supply", entity.medDaySupply.toString()] : undefined,
      entity.cancer ? ["Cancer", entity.cancer] : undefined,
      entity.cancerType ? ["Cancer Type", entity.cancerType] : undefined,
      entity.cancerSubtype ? ["Cancer Subtype", entity.cancerSubtype] : undefined,
      entity.cancerStage ? ["Stage", entity.cancerStage] : undefined,
      entity.cancerRecurrence ? ["Recurrence", entity.cancerRecurrence ? "Yes" : "No"] : undefined,
    ]);

  return (
    <Flex width="100%">
      <TextField
        size="medium"
        multiline
        rows={2}
        value={query}
        onKeyPress={(evt) => {
          if (evt.key === "Enter") {
            getMedicalEntities();
            evt.preventDefault();
          }
        }}
        onChange={(evt) => setQuery(evt.target.value)}
      />
      {query && (
        <>
          <Flex component={Paper} p={1} m={1} justifyContent="center" alignItems="center">
            <AnnotatedBlock text={query} entities={detectionResult} />
          </Flex>
          <Flex component={Paper} p={1} m={1} justifyContent="center" alignItems="center">
            <P>GRAMS</P>
            <P variant="xs">{grams.join(", ")}</P>
          </Flex>
        </>
      )}
      <Flex>
        {detectionResult.map((entity, idx) => {
          const concept = conceptCache[entity.entity.id];
          return (
            <Flex m={1} p={2} component={Paper} key={idx}>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                <P>
                  <b>Alias used:</b> {entity.alias}
                </P>
                <P sx={{ ml: 1 }}>Score: {entity.adjustedScore?.toFixed(2)}</P>
                <Button size="small" disabled>
                  Request alias removal
                </Button>
              </Box>
              {concept && <WitsConceptInfo concept={concept} />}
              {additionalFields(entity).map(([name, value]) => {
                return (
                  <Typography key={name}>
                    {name}: {value}
                  </Typography>
                );
              })}
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

export default DetectionSandbox;
